import { authAPI } from '../../services/api';
import jwtDecode from 'jwt-decode';
import logger from '../../utils/logger';

// 定义动作类型
export const AUTH_START = 'AUTH_START';
export const AUTH_SUCCESS = 'AUTH_SUCCESS';
export const AUTH_FAIL = 'AUTH_FAIL';
export const AUTH_LOGOUT = 'AUTH_LOGOUT';
export const AUTH_CHECK = 'AUTH_CHECK';

// 设置cookie的辅助函数，有效期为7天
const setCookie = (name, value, days = 7) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

// 获取cookie的辅助函数
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// 删除cookie的辅助函数
const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

// 设置HTTP请求头中的认证Token，同时保存在localStorage和cookie中
const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    setCookie('token', token, 7); // 保存7天
  } else {
    localStorage.removeItem('token');
    deleteCookie('token');
  }
};

// 登录操作
export const login = (username, password) => {
  return async (dispatch) => {
    dispatch({ type: AUTH_START });
    
    try {
      const response = await authAPI.login(username, password);
      
      // 检查response的结构
      if (!response.success && response.message) {
        throw new Error(response.message);
      }

      // 确保response.data存在
      if (!response.data) {
        throw new Error('服务器返回数据格式不正确');
      }
      
      const { token, userId, username: user, points, userType } = response.data;
      
      // 设置Token
      setAuthToken(token);
      
      // 分发成功动作
      dispatch({
        type: AUTH_SUCCESS,
        payload: { 
          userId, 
          username: user, 
          points, 
          userType 
        }
      });
      
      return response;
    } catch (error) {
      logger.error('登录错误详情:', error);
      
      // 分发失败动作
      let errorMessage = '登录失败，请检查用户名和密码';
      
      if (error.response) {
        // 服务器回复了错误状态码
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 401) {
          errorMessage = '用户名或密码错误';
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后再试';
        }
      } else if (error.message) {
        // 使用错误对象自带的消息
        errorMessage = error.message;
      }
      
      dispatch({
        type: AUTH_FAIL,
        payload: errorMessage
      });
      
      throw error;
    }
  };
};

// 发送短信验证码
export const sendSmsCode = (phone, type = 'login') => {
  return async (dispatch) => {
    try {
      const response = await authAPI.sendSmsCode(phone, type);
      
      if (!response.success) {
        throw new Error(response.message || '发送验证码失败');
      }
      
      return response;
    } catch (error) {
      logger.error('发送验证码错误:', error);
      
      let errorMessage = '发送验证码失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '手机号格式错误或请求过于频繁';
        } else if (error.response.status === 401) {
          errorMessage = '需要先登录';
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后再试';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 手机号验证码登录
export const smsLogin = (phone, code) => {
  return async (dispatch) => {
    dispatch({ type: AUTH_START });
    
    try {
      const response = await authAPI.smsLogin(phone, code);
      
      // 检查response的结构
      if (!response.success && response.message) {
        throw new Error(response.message);
      }

      // 确保response.data存在
      if (!response.data) {
        throw new Error('服务器返回数据格式不正确');
      }
      
      const { token, userId, username, points, userType, phone: userPhone } = response.data;
      
      // 设置Token
      setAuthToken(token);
      
      // 分发成功动作
      dispatch({
        type: AUTH_SUCCESS,
        payload: { 
          userId, 
          username, 
          points, 
          userType,
          phone: userPhone
        }
      });
      
      return response;
    } catch (error) {
      logger.error('手机号登录错误详情:', error);
      
      // 分发失败动作
      let errorMessage = '登录失败，请检查手机号和验证码';
      
      if (error.response) {
        // 服务器回复了错误状态码
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '验证码错误或已过期';
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后再试';
        }
      } else if (error.message) {
        // 使用错误对象自带的消息
        errorMessage = error.message;
      }
      
      dispatch({
        type: AUTH_FAIL,
        payload: errorMessage
      });
      
      throw error;
    }
  };
};

// 退出登录
export const logout = () => {
  return async (dispatch) => {
    try {
      await authAPI.logout();
    } catch (error) {
      logger.error('退出登录请求出错:', error);
    }
    
    // 清除Token
    setAuthToken(null);
    
    // 分发登出动作
    dispatch({ type: AUTH_LOGOUT });
  };
};

// 检查认证状态
export const checkAuthStatus = () => {
  return async (dispatch) => {
    // 优先从localStorage获取token，如果没有则从cookie中获取
    let token = localStorage.getItem('token');
    if (!token) {
      token = getCookie('token');
      // 如果从cookie找到token，更新到localStorage
      if (token) {
        localStorage.setItem('token', token);
      }
    }
    
    if (!token) {
      dispatch({ type: AUTH_CHECK });
      return;
    }
    
    // 获取用户信息，如果失败则清理认证状态
    try {
      await dispatch(getUserInfo());
    } catch (error) {
      logger.error('检查认证状态时获取用户信息失败:', error);
      
      // 如果是403错误（账号被禁用），认证信息已在axios拦截器中清理
      // 如果是401错误或其他错误，也需要清理本地认证状态
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        deleteCookie('token');
        dispatch({ type: AUTH_LOGOUT });
      }
      
      // 标记认证检查完成
      dispatch({ type: AUTH_CHECK });
    }
  };
};

// 获取用户信息
export const getUserInfo = () => {
  return async (dispatch) => {
    try {
      const response = await authAPI.getUserInfo();
      
      if (!response.success) {
        throw new Error(response.message || '获取用户信息失败');
      }
      
      // 更新Redux store中的用户信息
      dispatch({
        type: AUTH_SUCCESS,
        payload: response.data
      });
      
      return response.data;
    } catch (error) {
      logger.error('获取用户信息错误:', error);
      
      // 如果是403错误（账号被禁用），清理认证状态
      if (error.response?.status === 403) {
        const errorMessage = error.response.data?.message || '';
        
        if (errorMessage.includes('账号已被禁用') || errorMessage.includes('账号被禁用')) {
          // 分发登出action，清理Redux状态
          dispatch({ type: AUTH_LOGOUT });
          
          // 不需要再次清理localStorage和cookie，axios拦截器已经处理了
          logger.warn('账号已被禁用，已清理认证状态');
        }
      }
      // 如果是401错误（token无效），也清理认证状态
      else if (error.response?.status === 401) {
        dispatch({ type: AUTH_LOGOUT });
        logger.warn('认证token无效，已清理认证状态');
      }
      
      throw error;
    }
  };
};

// 修改用户名
export const updateUsername = (username) => {
  return async (dispatch) => {
    try {
      const response = await authAPI.updateUsername(username);
      
      if (!response.success) {
        throw new Error(response.message || '修改用户名失败');
      }
      
      // 修改成功后重新获取用户信息以更新Redux store
      await dispatch(getUserInfo());
      
      return response.data;
    } catch (error) {
      logger.error('修改用户名错误:', error);
      
      let errorMessage = '修改用户名失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '用户名格式不正确或长度不符合要求';
        } else if (error.response.status === 409) {
          errorMessage = '用户名已存在，请选择其他用户名';
        } else if (error.response.status === 401) {
          errorMessage = '未登录或登录已过期';
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后再试';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 生成微信登录二维码
export const generateWechatQRCode = (type = 'login') => {
  return async (dispatch) => {
    try {
      const response = await authAPI.generateWechatQRCode(type);
      
      if (!response.success) {
        throw new Error(response.message || '生成二维码失败');
      }
      
      return response.data; // 返回 { scene_str, qr_url, expires_in }
    } catch (error) {
      //console.error('生成微信二维码错误:', error);
      
      let errorMessage = '生成微信二维码失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后再试';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 查询微信二维码状态
export const getWechatQRCodeStatus = (scene_str) => {
  return async (dispatch) => {
    try {
      const response = await authAPI.getWechatQRCodeStatus(scene_str);
      
      if (!response.success) {
        throw new Error(response.message || '查询二维码状态失败');
      }
      
      return response.data; // 返回 { status, message, token?, user? }
    } catch (error) {
      //console.error('查询微信二维码状态错误:', error);
      
      // 对于404错误（二维码不存在），返回特殊状态而不抛出错误
      if (error.response && error.response.status === 404) {
        return { status: 'not_found', message: '二维码不存在或已过期' };
      }
      
      let errorMessage = '查询二维码状态失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 微信登录（处理confirmed状态）
export const wechatLogin = (statusData) => {
  return async (dispatch) => {
    dispatch({ type: AUTH_START });
    
    try {
      // statusData应该包含token和user信息
      const { token, user } = statusData;
      
      if (!token || !user) {
        throw new Error('登录数据不完整');
      }
      
      // 设置Token
      setAuthToken(token);
      
      // 分发成功动作
      dispatch({
        type: AUTH_SUCCESS,
        payload: { 
          userId: user.userId, 
          username: user.username, 
          points: user.points, 
          userType: user.userType
        }
      });
      
      return { data: user, token };
    } catch (error) {
      //console.error('微信登录错误详情:', error);
      
      // 分发失败动作
      let errorMessage = '微信登录失败';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({
        type: AUTH_FAIL,
        payload: errorMessage
      });
      
      throw error;
    }
  };
};

// 绑定手机号
export const bindPhone = (phone, code) => {
  return async (dispatch) => {
    try {
      const response = await authAPI.bindPhone(phone, code);
      
      if (!response.success) {
        throw new Error(response.message || '手机号绑定失败');
      }
      
      // 绑定成功后重新获取用户信息
      await dispatch(getUserInfo());
      
      return response.data;
    } catch (error) {
      //console.error('绑定手机号错误:', error);
      
      let errorMessage = '手机号绑定失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '手机号格式错误或已被绑定';
        } else if (error.response.status === 401) {
          errorMessage = '验证码错误或已过期';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 验证当前手机号（修改手机号第一步）
export const verifyCurrentPhone = (code) => {
  return async (dispatch) => {
    try {
      const response = await authAPI.verifyCurrentPhone(code);
      
      if (!response.success) {
        throw new Error(response.message || '当前手机号验证失败');
      }
      
      return response.data; // 返回包含verificationId的数据
    } catch (error) {
      //console.error('验证当前手机号错误:', error);
      
      let errorMessage = '当前手机号验证失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '验证码错误或已过期';
        } else if (error.response.status === 401) {
          errorMessage = '需要先登录';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 验证新手机号并完成修改（修改手机号第二步）
export const verifyNewPhoneAndComplete = (phone, code, currentVerificationId) => {
  return async (dispatch) => {
    try {
      const response = await authAPI.verifyNewPhoneAndComplete(phone, code, currentVerificationId);
      
      if (!response.success) {
        throw new Error(response.message || '手机号修改失败');
      }
      
      // 修改成功后重新获取用户信息
      await dispatch(getUserInfo());
      
      return response.data;
    } catch (error) {
      //console.error('验证新手机号并完成修改错误:', error);
      
      let errorMessage = '手机号修改失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '验证码错误、已过期或验证ID无效';
        } else if (error.response.status === 409) {
          errorMessage = '该手机号已被其他账号绑定';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 绑定微信账号
export const bindWechat = (scene_str) => {
  return async (dispatch) => {
    try {
      const response = await authAPI.bindWechat(scene_str);
      
      if (!response.success) {
        throw new Error(response.message || '微信绑定失败');
      }
      
      // 绑定成功后重新获取用户信息
      await dispatch(getUserInfo());
      
      return response.data;
    } catch (error) {
      //console.error('绑定微信账号错误:', error);
      
      let errorMessage = '微信绑定失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '绑定会话无效或已过期';
        } else if (error.response.status === 404) {
          errorMessage = '绑定会话不存在';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
};

// 解除微信绑定
export const unbindWechat = () => {
  return async (dispatch) => {
    try {
      const response = await authAPI.unbindWechat();
      
      if (!response.success) {
        throw new Error(response.message || '解除微信绑定失败');
      }
      
      // 解绑成功后重新获取用户信息
      await dispatch(getUserInfo());
      
      return response.data;
    } catch (error) {
      //console.error('解除微信绑定错误:', error);
      
      let errorMessage = '解除微信绑定失败';
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = '解除绑定条件不满足，请确保已绑定手机号并至少保留一种登录方式';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };
}; 